// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as echarts from 'echarts';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import TechStack from '../components/TechStack';
import Services from '../components/Services';
import WorkProcess from '../components/WorkProcess';
import ContactForm from '../components/ContactForm';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [isHovering, setIsHovering] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const navigate = useNavigate();
  const { theme, currentTheme } = useTheme();
  const { currentLanguage } = useLanguage();

  // Preloader effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Calculate which section is in view
      const sections = ['home', 'about', 'services', 'tech-stack', 'work-process', 'contact'];
      let currentSection = 'home';
      
      // Определяем положение элементов и их границы
      const sectionElements = {};
      let hasVisibleSection = false;
      
      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          sectionElements[section] = rect;
          
          // Improve visibility detection for all sections
          // A section is considered visible when its top is near the viewport middle
          const isVisible = rect.top <= window.innerHeight * 0.5 && rect.bottom >= 0;
          
          if (isVisible) {
            currentSection = section;
            hasVisibleSection = true;
          }
        }
      });
      
      // Special case for home section
      if (window.scrollY < 300) {
        currentSection = 'home';
      }
      
      setActiveSection(currentSection);
    };
    
    window.addEventListener('scroll', handleScroll);
    // Вызываем обработчик сразу, чтобы установить начальное состояние
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Chart initialization
  useEffect(() => {
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
      
      const option = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: ['2020', '2021', '2022', '2023', '2024', '2025'],
            axisTick: {
              alignWithLabel: true
            },
            axisLine: {
              lineStyle: {
                color: '#00F0FF'
              }
            },
            axisLabel: {
              color: '#fff'
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            axisLine: {
              lineStyle: {
                color: '#00F0FF'
              }
            },
            axisLabel: {
              color: '#fff'
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(0, 240, 255, 0.1)'
              }
            }
          }
        ],
        series: [
          {
            name: 'Услуги',
            type: 'bar',
            barWidth: '60%',
            data: [10, 25, 42, 68, 89, 120],
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#FF00F5' },
                { offset: 1, color: '#00F0FF' }
              ])
            },
            animationDelay: function (idx) {
              return idx * 100;
            }
          }
        ]
      };

      chartInstance.current.setOption(option);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [isLoading]);

  // Handle window resize for chart
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Варианты анимаций для Framer Motion
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Функции действий для кнопок
  const learnMoreAbout = () => {
    scrollToSection('about');
  };
  
  const viewServices = (serviceId) => {
    scrollToSection('services');
    // Можно добавить логику для отображения конкретной услуги
  };

  // Получение текстов с учетом языка
  const getText = () => {
    const texts = {
      ru: {
        statsTitle: "Наш рост",
        statsDescription: "За последние годы мы значительно выросли, расширили команду и портфолио проектов. Наши клиенты - от стартапов до крупных корпораций - доверяют нам свои цифровые продукты.",
        projects: "Завершенных проектов",
        clients: "Довольных клиентов",
        team: "Профессионалов в команде",
        awards: "Отраслевых наград",
        ctaTitle: "Готовы создать идеальный сайт?",
        ctaDescription: "Свяжитесь с нами сегодня, и мы поможем воплотить в жизнь ваши цифровые идеи",
        discussProject: "Обсудить проект"
      },
      en: {
        statsTitle: "Our Growth",
        statsDescription: "Over the past years, we have grown significantly, expanded our team and project portfolio. Our clients - from startups to large corporations - trust us with their digital products.",
        projects: "Completed projects",
        clients: "Satisfied clients",
        team: "Professionals in the team",
        awards: "Industry awards",
        ctaTitle: "Ready to create the perfect website?",
        ctaDescription: "Contact us today and we'll help bring your digital ideas to life",
        discussProject: "Discuss a project"
      }
    };
    
    return texts[currentLanguage] || texts.ru;
  };
  
  const texts = getText();

  if (isLoading) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center ${currentTheme === 'light' ? 'bg-white' : 'bg-black'} z-50`}>
        <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
          {/* Анимированный градиентный фон */}
          <div className={`absolute inset-0 ${currentTheme === 'light' ? 'bg-[#F5F7FA]' : 'bg-[#050505]'}`}>
            <div className="absolute top-0 left-0 w-full h-full">
              <div className={`absolute w-[50vw] h-[50vh] rounded-full ${
                currentTheme === 'light' 
                ? 'bg-gradient-to-r from-[#1A56DB]/20 via-[#1A56DB]/10 to-transparent'
                : 'bg-gradient-to-r from-dark-primary/20 via-dark-primary/10 to-transparent'
              } -top-[25vh] -left-[25vw] blur-[60px] animate-pulse`}></div>
              <div className={`absolute w-[50vw] h-[50vh] rounded-full ${
                currentTheme === 'light'
                ? 'bg-gradient-to-r from-[#7E22CE]/20 via-[#7E22CE]/10 to-transparent'
                : 'bg-gradient-to-r from-dark-secondary/20 via-dark-secondary/10 to-transparent'
              } -bottom-[25vh] -right-[25vw] blur-[60px] animate-pulse`}></div>
              <div className={`absolute inset-0 ${currentTheme === 'light' ? 'bg-[#F5F7FA]/30' : 'bg-[#050505]/30'}`}></div>
            </div>
          </div>
          
          {/* Пиксельная сетка */}
          <div className={`absolute inset-0 ${
            currentTheme === 'light'
            ? 'opacity-20 bg-dots-light'
            : 'opacity-20 bg-dots-dark'
          }`}></div>
          
          {/* Логотип */}
          <div className="relative z-10">
            <div className="relative flex flex-col items-center">
              {/* Создаем блики */}
              <div className="absolute inset-0 blur-[50px]">
                <div className={`h-full w-full ${
                  currentTheme === 'light'
                  ? 'bg-gradient-to-r from-[#1A56DB]/0 via-[#1A56DB]/40 to-[#1A56DB]/0'
                  : 'bg-gradient-to-r from-dark-primary/0 via-dark-primary/40 to-dark-primary/0'
                }`}></div>
              </div>
              
              {/* Логотип с глитч-эффектом */}
              <div className={`${
                currentTheme === 'light'
                ? 'text-[#1A56DB]'
                : 'text-dark-primary'
              } text-6xl md:text-8xl font-bold tracking-wider mb-1 relative`}>
                <span className="relative z-10">Movi</span>
                <span className={`${
                  currentTheme === 'light'
                  ? 'text-[#7E22CE]'
                  : 'text-dark-secondary'
                } relative z-10`}>Bobby</span>
                <div className={`absolute top-0 left-0 w-full h-full ${
                  currentTheme === 'light'
                  ? 'text-[#7E22CE] opacity-70 blur-[1px]'
                  : 'text-dark-secondary opacity-70 blur-[1px]'
                } transform translate-x-[2px] translate-y-[2px]`}>
                  Movi<span className={`${
                    currentTheme === 'light'
                    ? 'text-[#1A56DB]'
                    : 'text-dark-primary'
                  }`}>Bobby</span>
                </div>
              </div>
              
              <div className={`${
                currentTheme === 'light'
                ? 'text-gray-600'
                : 'text-gray-400'
              } tracking-[0.5em] text-sm uppercase mt-4`}>РАЗРАБОТКА САЙТОВ</div>
            </div>
          </div>
          
          {/* Индикатор загрузки */}
          <div className="relative z-10 mt-16 w-64 h-[2px]">
            <div className={`absolute top-0 left-0 h-full w-10 ${
              currentTheme === 'light'
              ? 'bg-gradient-to-r from-transparent via-[#1A56DB] to-transparent'
              : 'bg-gradient-to-r from-transparent via-dark-primary to-transparent'
            } animate-loading-bar`}></div>
            <div className={`absolute top-0 left-0 h-full w-full ${
              currentTheme === 'light'
              ? 'bg-gray-200'
              : 'bg-white/10'
            }`}></div>
          </div>
          
          {/* Текст загрузки */}
          <div className={`relative z-10 mt-8 ${
            currentTheme === 'light'
            ? 'text-gray-600'
            : 'text-gray-400'
          } flex items-center`}>
            <span className="mr-2">загрузка</span>
            <span className={`inline-block h-2 w-2 ${
              currentTheme === 'light'
              ? 'bg-[#1A56DB]'
              : 'bg-dark-primary'
            } rounded-full animate-dot1 mr-1`}></span>
            <span className={`inline-block h-2 w-2 ${
              currentTheme === 'light'
              ? 'bg-[#7E22CE]'
              : 'bg-dark-secondary'
            } rounded-full animate-dot2 mr-1`}></span>
            <span className={`inline-block h-2 w-2 ${
              currentTheme === 'light'
              ? 'bg-[#1A56DB]'
              : 'bg-dark-primary'
            } rounded-full animate-dot3`}></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${currentTheme === 'light' ? 'bg-light-background text-light-text' : 'bg-[#050505] text-white'} min-h-screen overflow-x-hidden`}>
      {/* Декоративные элементы фона */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Градиентный круг в верхнем левом углу */}
        <div 
          className={`absolute -top-32 -left-32 w-96 h-96 rounded-full ${
            currentTheme === 'light' 
              ? 'bg-gradient-to-r from-light-primary/10 to-light-secondary/10 blur-3xl opacity-20' 
              : 'bg-gradient-to-r from-dark-primary/20 to-dark-secondary/20 blur-3xl opacity-30'
          }`}
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        ></div>
        
        {/* Градиентный круг в правом нижнем углу */}
        <div 
          className={`absolute -bottom-32 -right-32 w-96 h-96 rounded-full ${
            currentTheme === 'light' 
              ? 'bg-gradient-to-r from-light-secondary/10 to-light-primary/10 blur-3xl opacity-20' 
              : 'bg-gradient-to-r from-dark-secondary/20 to-dark-primary/20 blur-3xl opacity-30'
          }`}
          style={{ transform: `translateY(-${scrollY * 0.05}px)` }}
        ></div>
        
        {/* Сетка из точек (имитация grid) */}
        <div className={`absolute inset-0 ${
          currentTheme === 'light' 
            ? 'opacity-5 bg-dots-light' 
            : 'opacity-10 bg-dots-dark'
        }`}></div>
      </div>

      {/* Main Content Sections */}
      
      {/* Using Navbar component instead of inline header */}
      <Navbar activeSection={activeSection} scrollToSection={scrollToSection} />
      
      {/* Using HeroSection component instead of inline hero section */}
      <HeroSection />

      {/* About Section */}
      <section id="about" className={`py-20 relative overflow-hidden ${currentTheme === 'light' ? 'bg-light-background' : 'bg-[#070707]'}`}>
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center">
            <motion.div 
              className="md:w-1/2 mb-12 md:mb-0 md:pr-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
            >
              <h3 className="text-3xl font-bold mb-6">{texts.statsTitle}</h3>
              <p className={currentTheme === 'light' ? 'text-gray-600 mb-6' : 'text-gray-400 mb-6'}>
                {texts.statsDescription}
              </p>
              <motion.div 
                className="grid grid-cols-2 gap-6"
                variants={staggerContainer}
              >
                <motion.div 
                  className={`${
                    currentTheme === 'light' 
                      ? 'bg-white p-6 rounded-xl border border-light-primary/20 hover:border-light-primary/40 shadow-sm' 
                      : 'bg-[#0A0A0A] p-6 rounded-xl border border-dark-primary/20 hover:border-dark-primary/40'
                  } transition-all duration-300`}
                  variants={fadeInUp}
                >
                  <div className={`text-4xl font-bold mb-2 ${
                    currentTheme === 'light' ? 'text-light-primary' : 'text-dark-primary'
                  }`}>150+</div>
                  <div className={currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'}>{texts.projects}</div>
                </motion.div>
                <motion.div 
                  className={`${
                    currentTheme === 'light' 
                      ? 'bg-white p-6 rounded-xl border border-light-secondary/20 hover:border-light-secondary/40 shadow-sm' 
                      : 'bg-[#0A0A0A] p-6 rounded-xl border border-dark-secondary/20 hover:border-dark-secondary/40'
                  } transition-all duration-300`}
                  variants={fadeInUp}
                >
                  <div className={`text-4xl font-bold mb-2 ${
                    currentTheme === 'light' ? 'text-light-secondary' : 'text-dark-secondary'
                  }`}>98%</div>
                  <div className={currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'}>{texts.clients}</div>
                </motion.div>
                <motion.div 
                  className={`${
                    currentTheme === 'light' 
                      ? 'bg-white p-6 rounded-xl border border-light-secondary/20 hover:border-light-secondary/40 shadow-sm' 
                      : 'bg-[#0A0A0A] p-6 rounded-xl border border-dark-secondary/20 hover:border-dark-secondary/40'
                  } transition-all duration-300`}
                  variants={fadeInUp}
                >
                  <div className={`text-4xl font-bold mb-2 ${
                    currentTheme === 'light' ? 'text-light-secondary' : 'text-dark-secondary'
                  }`}>25+</div>
                  <div className={currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'}>{texts.team}</div>
                </motion.div>
                <motion.div 
                  className={`${
                    currentTheme === 'light' 
                      ? 'bg-white p-6 rounded-xl border border-light-primary/20 hover:border-light-primary/40 shadow-sm' 
                      : 'bg-[#0A0A0A] p-6 rounded-xl border border-dark-primary/20 hover:border-dark-primary/40'
                  } transition-all duration-300`}
                  variants={fadeInUp}
                >
                  <div className={`text-4xl font-bold mb-2 ${
                    currentTheme === 'light' ? 'text-light-primary' : 'text-dark-primary'
                  }`}>12</div>
                  <div className={currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'}>{texts.awards}</div>
                </motion.div>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="md:w-1/2 md:h-[400px] h-0" 
              ref={chartRef}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            ></motion.div>
          </div>
        </div>
        
        {/* Декоративные линии */}
        <div className={`absolute -left-20 top-1/4 w-40 h-1 bg-gradient-to-r ${
          currentTheme === 'light' 
            ? 'from-light-primary to-transparent opacity-20' 
            : 'from-dark-primary to-transparent opacity-30'
        }`}></div>
        <div className={`absolute -right-20 bottom-1/4 w-40 h-1 bg-gradient-to-l ${
          currentTheme === 'light' 
            ? 'from-light-secondary to-transparent opacity-20' 
            : 'from-dark-secondary to-transparent opacity-30'
        }`}></div>
      </section>

      {/* Projects Section */}
      <section id="services" className={`py-20 relative ${currentTheme === 'light' ? 'bg-gray-50' : 'bg-[#070707]'}`}>
        {/* Декоративные элементы */}
        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent ${
          currentTheme === 'light' 
            ? 'via-light-primary/30' 
            : 'via-dark-primary/30'
        } to-transparent`}></div>
        <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent ${
          currentTheme === 'light' 
            ? 'via-light-secondary/30' 
            : 'via-dark-secondary/30'
        } to-transparent`}></div>
        
        {/* Декоративные элементы фона */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-40 -right-40 w-96 h-96 rounded-full ${
            currentTheme === 'light'
              ? 'bg-gradient-to-br from-light-primary/5 to-light-secondary/5'
              : 'bg-gradient-to-br from-dark-primary/5 to-dark-secondary/5'
          } blur-3xl`}></div>
          <div className={`absolute -bottom-20 -left-20 w-80 h-80 rounded-full ${
            currentTheme === 'light'
              ? 'bg-gradient-to-br from-light-secondary/5 to-light-primary/5'
              : 'bg-gradient-to-br from-dark-secondary/5 to-dark-primary/5'
          } blur-3xl`}></div>
        </div>
        
        <div className="container mx-auto px-6">
          {/* CTA секция */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={fadeInUp}
            className={`relative overflow-hidden rounded-3xl ${
              currentTheme === 'light'
                ? 'bg-gradient-to-r from-light-primary to-light-secondary'
                : 'bg-gradient-to-r from-dark-primary to-dark-secondary'
            }`}
          >
            {/* Декоративные элементы */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>
            </div>
            
            <div className="relative z-10 p-8 md:p-16 text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                {texts.ctaTitle}
              </h3>
              <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
                {texts.ctaDescription}
              </p>
              
              <motion.button
                onClick={() => scrollToSection('contact')}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: currentTheme === 'light' 
                    ? '0 10px 40px -10px rgba(66, 71, 237, 0.5)' 
                    : '0 10px 40px -10px rgba(0, 240, 255, 0.5)'
                }}
                whileTap={{ scale: 0.98 }}
                className={`px-8 py-4 rounded-full font-medium ${
                  currentTheme === 'light'
                    ? 'bg-white text-light-primary hover:text-light-secondary'
                    : 'bg-[#0A0A0A] text-white'
                } transition-all duration-300 shadow-lg`}
              >
                {texts.discussProject}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Import component-based sections instead of redefining them inline */}
      <Services />
      <TechStack />
      <WorkProcess />
      <ContactForm />
      
      {/* Using Footer component instead of inline footer */}
      <Footer />
      
      {/* Scroll To Top Button */}
      <motion.button
        className={`fixed bottom-8 right-8 w-12 h-12 rounded-full ${
          currentTheme === 'light' 
            ? 'bg-gradient-to-r from-light-primary to-light-secondary text-white' 
            : 'bg-gradient-to-r from-dark-primary to-dark-secondary text-white'
        } flex items-center justify-center shadow-lg z-40`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: scrollY > 300 ? 1 : 0,
          y: scrollY > 300 ? 0 : 20 
        }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <i className="fas fa-arrow-up"></i>
      </motion.button>
    </div>
  );
}

export default HomePage;